import { Select, MenuItem, FormControl } from '@mui/material';
import { banksForTerm, rateFor, BANK_PLANS, TERMINAL } from '../../utils/ccBanks';

/**
 * THE bank picker. One component, used by the RSC calculator, Create Sale and the edit
 * sale page, because RSC is the source of truth for how pricing is chosen and the other
 * screens must follow the same model rather than keep their own copy of it.
 * (user 2026-10-29: "rsc shd be similar model as its source of truth")
 *
 * It owns the whole rule, so there is nowhere for the screens to drift apart:
 *   - which banks offer the term at all (only six do 60 months)
 *   - a bank whose rate depends on the method appears once per method
 *   - cheapest first, so the best plan for the customer is the top row
 *   - the value is "<bank>" or "<bank>::terminal" / "<bank>::link"
 *
 * `value`/`method` in, `onChange(bank, method)` out — the caller keeps the state, so the
 * same component serves a calculator card and a form field without knowing either.
 */
export default function CcBankSelect({
    term, value = '', method = '', onChange,
    disabled = false, required = false, error = false,
    sx, dark = false, t = (en) => en,
}) {
    if (!(term > 0)) return null;

    const options = [];
    banksForTerm(term).forEach((bank) => {
        const plan = BANK_PLANS[bank][term];
        if (plan.rateByMethod) {
            plan.methods.forEach(m => options.push({ value: `${bank}::${m}`, bank, method: m, rate: plan.rateByMethod[m] }));
        } else {
            options.push({ value: bank, bank, method: '', rate: rateFor(bank, term) });
        }
    });
    options.sort((a, b) => a.rate - b.rate || a.bank.localeCompare(b.bank));

    const current = value ? (method ? `${value}::${method}` : value) : '';
    const hint = dark ? 'rgba(255,255,255,0.55)' : '#64748B';

    return (
        <FormControl size="small" fullWidth error={error}>
            <Select size="small" fullWidth displayEmpty disabled={disabled} value={current} sx={sx}
                onChange={(e) => {
                    const [b, m] = String(e.target.value || '').split('::');
                    onChange && onChange(b || '', m || '');
                }}>
                <MenuItem value="">
                    <span style={{ color: dark ? 'rgba(255,255,255,0.45)' : '#94A3B8' }}>
                        {required ? t('Select a bank') : t('Any bank (same rate)')}
                    </span>
                </MenuItem>
                {options.map(o => (
                    <MenuItem key={o.value} value={o.value}>
                        {o.bank}
                        <span style={{ color: hint, fontSize: '0.8em', marginLeft: 6 }}>
                            {o.rate.toFixed(2)}%
                            {o.method ? ` · ${o.method === TERMINAL ? t('Terminal') : t('Payment Link')}` : ''}
                        </span>
                    </MenuItem>
                ))}
            </Select>
        </FormControl>
    );
}
